import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { musicalService } from '../../services/musicalService';
import { userService } from '../../services/userService';
import { useAuth } from "../../hooks/useAuth";
import Header from "../../components/ui/Header";
import Icon from '../../components/AppIcon';

// ─── Media Badges ─────────────────────────────────────────────────────────────
const MediaBadges = ({ musical, compact = false }) => {
  const badges = [
    {
      key: 'videos',
      icon: 'Video',
      label: 'Videos',
      count: musical?.videos_count || 0,
      activeColor: 'bg-purple-500 bg-opacity-30 text-purple-200 border-purple-400 border-opacity-40',
      inactiveColor: 'bg-white bg-opacity-5 text-blue-300 border-white border-opacity-10',
    },
    {
      key: 'midi',
      icon: 'Music2',
      label: 'MIDI',
      count: musical?.midi_count || 0,
      activeColor: 'bg-green-500 bg-opacity-30 text-green-200 border-green-400 border-opacity-40',
      inactiveColor: 'bg-white bg-opacity-5 text-blue-300 border-white border-opacity-10',
    },
    {
      key: 'mp3',
      icon: 'Headphones',
      label: 'MP3',
      count: musical?.mp3_count || 0,
      activeColor: 'bg-orange-500 bg-opacity-30 text-orange-200 border-orange-400 border-opacity-40',
      inactiveColor: 'bg-white bg-opacity-5 text-blue-300 border-white border-opacity-10',
    },
    {
      key: 'lessons',
      icon: 'BookOpen',
      label: 'Lessons',
      count: musical?.lessons_count || 0,
      activeColor: 'bg-blue-500 bg-opacity-30 text-blue-200 border-blue-400 border-opacity-40',
      inactiveColor: 'bg-white bg-opacity-5 text-blue-300 border-white border-opacity-10',
    },
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {badges?.map(badge => {
          const hasContent = badge?.count > 0;
          return (
            <span
              key={badge?.key}
              title={`${badge?.count} ${badge?.label}`}
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs border font-medium transition-colors ${
                hasContent ? badge?.activeColor : badge?.inactiveColor + ' opacity-40'
              }`}
            >
              <Icon name={badge?.icon} size={10} />
              {hasContent && <span>{badge?.count}</span>}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 flex-wrap mt-2">
      {badges?.map(badge => {
        const hasContent = badge?.count > 0;
        return (
          <span
            key={badge?.key}
            title={`${badge?.count} ${badge?.label}`}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs border font-medium transition-colors ${
              hasContent ? badge?.activeColor : badge?.inactiveColor + ' opacity-40'
            }`}
          >
            <Icon name={badge?.icon} size={10} />
            <span>{hasContent ? `${badge?.count} ${badge?.label}` : badge?.label}</span>
          </span>
        );
      })}
    </div>
  );
};

// ─── Musical Card Component ───────────────────────────────────────────────────
const MusicalCard = ({ musical, onClick, size = 'normal', isFavorited, onFavoriteToggle, isFeaturedMusical, onFeatureToggle, isSuperUser }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const posterUrl = musical?.poster_url
    ? (musical?.poster_url?.includes('?')
        ? `${musical?.poster_url}&t=${Date.now()}`
        : `${musical?.poster_url}?t=${Date.now()}`)
    : '';

  const getGenreLabel = (genre) => {
    const genreMap = {
      broadway: 'Broadway', off_broadway: 'Off-Broadway', community: 'Community',
      school: 'School', opera: 'Opera', operetta: 'Operetta',
      jukebox: 'Jukebox', rock: 'Rock', pop: 'Pop', classical: 'Classical'
    };
    return genreMap?.[genre] || 'Musical';
  };

  const isFeatured = size === 'featured';

  return (
    <div
      onClick={() => onClick?.(musical)}
      className={`group cursor-pointer bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl overflow-hidden hover:bg-opacity-15 hover:border-opacity-40 transition-all duration-200 ${
        isFeatured ? 'flex flex-col' : 'flex flex-col'
      }`}
    >
      {/* Poster */}
      <div className={`relative flex-shrink-0 bg-white bg-opacity-5 ${
        isFeatured ? 'h-56' : 'h-40'
      }`}>
        {posterUrl && !imageError ? (
          <>
            <img
              src={posterUrl}
              alt={`${musical?.title} poster`}
              className={`w-full h-full object-cover transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onError={() => setImageError(true)}
              onLoad={() => setImageLoaded(true)}
              loading="lazy"
            />
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-blue-300 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name="Music" size={isFeatured ? 40 : 28} className="text-blue-300 opacity-50" />
          </div>
        )}
        {/* Genre badge */}
        <span className="absolute top-2 left-2 px-2 py-0.5 bg-black bg-opacity-60 text-white text-xs rounded-full">
          {getGenreLabel(musical?.genre)}
        </span>
      </div>
      {/* Info */}
      <div className="p-3 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-1">
          <h3 className={`font-semibold text-white group-hover:text-blue-100 transition-colors leading-tight ${
            isFeatured ? 'text-base' : 'text-sm'
          }`}>
            {musical?.title}
          </h3>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isSuperUser?.() && onFeatureToggle && (
              <button
                onClick={(e) => { e?.stopPropagation(); onFeatureToggle(musical); }}
                className="p-0.5 rounded hover:bg-white hover:bg-opacity-10 transition-colors"
                title={isFeaturedMusical ? 'Remove from featured' : 'Mark as featured'}
              >
                <Icon
                  name="Star"
                  size={14}
                  className={isFeaturedMusical ? 'text-yellow-400' : 'text-blue-300 opacity-60'}
                  style={isFeaturedMusical ? { fill: 'currentColor' } : {}}
                />
              </button>
            )}
            {onFavoriteToggle && (
              <button
                onClick={(e) => { e?.stopPropagation(); onFavoriteToggle(musical); }}
                className="p-0.5 rounded hover:bg-white hover:bg-opacity-10 transition-colors"
                title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Icon
                  name="Heart"
                  size={14}
                  className={isFavorited ? 'text-red-400 fill-red-400' : 'text-blue-300 opacity-60'}
                  style={isFavorited ? { fill: 'currentColor' } : {}}
                />
              </button>
            )}
          </div>
        </div>
        {(musical?.composer || musical?.year_premiered) && (
          <p className="text-xs text-blue-200 mt-1 truncate">
            {musical?.composer && <span>{musical?.composer}</span>}
            {musical?.composer && musical?.year_premiered && <span> · </span>}
            {musical?.year_premiered && <span>{musical?.year_premiered}</span>}
          </p>
        )}
        <MediaBadges musical={musical} />
      </div>
    </div>
  );
};

// ─── Musical Row Item (for search results) ────────────────────────────────────
const MusicalRowItem = ({ musical, onClick, isFavorited, onFavoriteToggle, isFeaturedMusical, onFeatureToggle, isSuperUser }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const posterUrl = musical?.poster_url
    ? (musical?.poster_url?.includes('?')
        ? `${musical?.poster_url}&t=${Date.now()}`
        : `${musical?.poster_url}?t=${Date.now()}`)
    : '';

  const getGenreLabel = (genre) => {
    const genreMap = {
      broadway: 'Broadway', off_broadway: 'Off-Broadway', community: 'Community',
      school: 'School', opera: 'Opera', operetta: 'Operetta',
      jukebox: 'Jukebox', rock: 'Rock', pop: 'Pop', classical: 'Classical'
    };
    return genreMap?.[genre] || 'Musical';
  };

  return (
    <div
      onClick={() => onClick?.(musical)}
      className="group cursor-pointer bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-lg px-4 py-3 hover:bg-opacity-15 hover:border-opacity-30 transition-all duration-200 flex items-center space-x-4"
    >
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-10 h-14 bg-white bg-opacity-10 rounded overflow-hidden relative">
        {posterUrl && !imageError ? (
          <>
            <img
              src={posterUrl}
              alt={`${musical?.title} poster`}
              className={`w-full h-full object-cover transition-opacity duration-200 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onError={() => setImageError(true)}
              onLoad={() => setImageLoaded(true)}
              loading="lazy"
            />
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-3 h-3 border-2 border-blue-300 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name="Music" size={14} className="text-blue-300 opacity-50" />
          </div>
        )}
      </div>
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-semibold text-white group-hover:text-blue-100 transition-colors truncate">
            {musical?.title}
          </h3>
          {musical?.year_premiered && (
            <span className="text-xs text-blue-300 flex-shrink-0">({musical?.year_premiered})</span>
          )}
        </div>
        <div className="flex items-center space-x-3 mt-0.5">
          {musical?.composer && (
            <span className="text-xs text-blue-200 truncate">{musical?.composer}</span>
          )}
          <span className="text-xs text-blue-300 flex-shrink-0 px-1.5 py-0.5 bg-blue-500 bg-opacity-20 rounded">
            {getGenreLabel(musical?.genre)}
          </span>
        </div>
        <MediaBadges musical={musical} compact />
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {isSuperUser?.() && onFeatureToggle && (
          <button
            onClick={(e) => { e?.stopPropagation(); onFeatureToggle(musical); }}
            className="flex-shrink-0 p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-colors"
            title={isFeaturedMusical ? 'Remove from featured' : 'Mark as featured'}
          >
            <Icon
              name="Star"
              size={16}
              className={isFeaturedMusical ? 'text-yellow-400' : 'text-blue-300 opacity-60'}
              style={isFeaturedMusical ? { fill: 'currentColor' } : {}}
            />
          </button>
        )}
        {onFavoriteToggle && (
          <button
            onClick={(e) => { e?.stopPropagation(); onFavoriteToggle(musical); }}
            className="flex-shrink-0 p-1.5 rounded hover:bg-white hover:bg-opacity-10 transition-colors"
            title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Icon
              name="Heart"
              size={16}
              className={isFavorited ? 'text-red-400' : 'text-blue-300 opacity-60'}
              style={isFavorited ? { fill: 'currentColor' } : {}}
            />
          </button>
        )}
      </div>
      <Icon name="ChevronRight" size={16} className="text-blue-300 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
};

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title, count }) => (
  <div className="flex items-center space-x-3 mb-4">
    <div className="w-8 h-8 bg-blue-500 bg-opacity-30 rounded-lg flex items-center justify-center">
      <Icon name={icon} size={16} className="text-blue-200" />
    </div>
    <h2 className="text-lg font-semibold text-white">{title}</h2>
    {count !== undefined && (
      <span className="px-2 py-0.5 bg-white bg-opacity-10 text-blue-200 text-xs rounded-full">{count}</span>
    )}
  </div>
);

// ─── Loading Skeleton ─────────────────────────────────────────────────────────
const CardSkeleton = ({ height = 'h-40' }) => (
  <div className="bg-white bg-opacity-10 rounded-xl overflow-hidden animate-pulse">
    <div className={`${height} bg-white bg-opacity-10`} />
    <div className="p-3 space-y-2">
      <div className="h-3 bg-white bg-opacity-10 rounded w-3/4" />
      <div className="h-2 bg-white bg-opacity-10 rounded w-1/2" />
    </div>
  </div>
);

const RowSkeleton = () => (
  <div className="bg-white bg-opacity-10 rounded-lg px-4 py-3 flex items-center space-x-4 animate-pulse">
    <div className="w-10 h-14 bg-white bg-opacity-10 rounded flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-3 bg-white bg-opacity-10 rounded w-2/3" />
      <div className="h-2 bg-white bg-opacity-10 rounded w-1/3" />
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AllMusicals() {
  const navigate = useNavigate();
  const { user, isSuperUser } = useAuth();

  // All musicals loaded once
  const [allMusicals, setAllMusicals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Favorites: Set of favorited musical IDs
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesError, setFavoritesError] = useState(null);

  // Featured: Set of featured musical IDs (superuser only)
  const [featuredMusicalIds, setFeaturedMusicalIds] = useState(new Set());

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Load all musicals on mount
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await musicalService?.getMusicals();
        if (fetchError) {
          setError('Failed to load musicals. Please try again.');
          setAllMusicals([]);
        } else {
          const musicals = data || [];
          setAllMusicals(musicals);
          setFeaturedMusicalIds(new Set(musicals?.filter(m => m?.is_featured)?.map(m => m?.id)));
        }
      } catch (err) {
        console.error('Error loading musicals:', err);
        setError('Unable to load musicals. Please refresh the page.');
        setAllMusicals([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Load favorites when user is available
  useEffect(() => {
    if (!user?.id) return;
    const loadFavorites = async () => {
      setFavoritesLoading(true);
      setFavoritesError(null);
      try {
        const { favorites, error: favError } = await userService?.getFavoriteMusicals(user?.id);
        if (favError) {
          setFavoritesError('Failed to load favorites. Please try again.');
        } else if (favorites) {
          setFavoriteIds(new Set(favorites?.map(f => f?.musicalId)));
        }
      } catch (err) {
        console.error('Error loading favorites:', err);
        setFavoritesError('Unable to load favorites. Please try again.');
      } finally {
        setFavoritesLoading(false);
      }
    };
    loadFavorites();
  }, [user?.id]);

  // Toggle favorite for a musical
  const handleFavoriteToggle = useCallback(async (musical) => {
    if (!user?.id) return;
    const musicalId = musical?.id;
    const isCurrentlyFavorited = favoriteIds?.has(musicalId);

    // Optimistic update
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (isCurrentlyFavorited) {
        next?.delete(musicalId);
      } else {
        next?.add(musicalId);
      }
      return next;
    });

    try {
      if (isCurrentlyFavorited) {
        await userService?.removeFavoriteMusical(user?.id, musicalId);
      } else {
        await userService?.addFavoriteMusical(user?.id, musicalId);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      // Revert on error
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (isCurrentlyFavorited) {
          next?.add(musicalId);
        } else {
          next?.delete(musicalId);
        }
        return next;
      });
    }
  }, [user?.id, favoriteIds]);

  // Toggle featured for a musical (superuser only)
  const handleFeatureToggle = useCallback(async (musical) => {
    if (!isSuperUser?.()) return;
    const musicalId = musical?.id;
    const isCurrentlyFeatured = featuredMusicalIds?.has(musicalId);
    const newFeaturedValue = !isCurrentlyFeatured;

    // Optimistic update
    setFeaturedMusicalIds(prev => {
      const next = new Set(prev);
      if (isCurrentlyFeatured) {
        next?.delete(musicalId);
      } else {
        next?.add(musicalId);
      }
      return next;
    });
    // Also update allMusicals so derived sections reflect change
    setAllMusicals(prev => prev?.map(m => m?.id === musicalId ? { ...m, is_featured: newFeaturedValue } : m));

    try {
      await musicalService?.updateMusical(musicalId, { is_featured: newFeaturedValue, updated_by: user?.id });
    } catch (err) {
      console.error('Error toggling featured:', err);
      // Revert on error
      setFeaturedMusicalIds(prev => {
        const next = new Set(prev);
        if (isCurrentlyFeatured) {
          next?.add(musicalId);
        } else {
          next?.delete(musicalId);
        }
        return next;
      });
      setAllMusicals(prev => prev?.map(m => m?.id === musicalId ? { ...m, is_featured: isCurrentlyFeatured } : m));
    }
  }, [isSuperUser, featuredMusicalIds, user?.id]);

  // ── Derived sections ──────────────────────────────────────────────────────

  // Featured: musicals that have is_featured set to true
  const featuredMusicals = useMemo(() => {
    return allMusicals?.filter(m => m?.is_featured);
  }, [allMusicals]);

  // Recently updated: top 8 by updated_at desc
  const recentMusicals = useMemo(() => {
    return [...allMusicals]?.sort((a, b) => {
        const dateA = new Date(a?.updated_at || a?.created_at || 0);
        const dateB = new Date(b?.updated_at || b?.created_at || 0);
        return dateB - dateA;
      })?.slice(0, 8);
  }, [allMusicals]);

  // IDs already shown in featured + recent (to exclude from search)
  const shownIds = useMemo(() => {
    const ids = new Set();
    featuredMusicals?.forEach(m => ids?.add(m?.id));
    recentMusicals?.forEach(m => ids?.add(m?.id));
    return ids;
  }, [featuredMusicals, recentMusicals]);

  // Search results: fuzzy filter over ALL musicals (including featured/recent)
  const searchResults = useMemo(() => {
    if (!searchTerm?.trim()) return [];
    const q = searchTerm?.toLowerCase()?.trim();
    const words = q?.split(/\s+/);
    return allMusicals?.filter(m => {
      const text = [
        m?.title, m?.composer, m?.lyricist, m?.book_writer, m?.description, m?.genre
      ]?.filter(Boolean)?.join(' ')?.toLowerCase();
      return words?.every(w => text?.includes(w));
    });
  }, [allMusicals, searchTerm]);

  // Remaining musicals (not in featured or recent) shown when no search
  const remainingMusicals = useMemo(() => {
    return allMusicals?.filter(m => !shownIds?.has(m?.id));
  }, [allMusicals, shownIds]);

  const handleMusicalClick = useCallback((musical) => {
    navigate(`/add-musical/${musical?.id}`);
  }, [navigate]);

  const handleSearchChange = (e) => {
    setSearchInput(e?.target?.value);
    setSearchTerm(e?.target?.value);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  const isSearching = searchTerm?.trim()?.length > 0;

  // Favorites musicals: full musical objects for favorited IDs
  const favoriteMusicals = useMemo(() => {
    return allMusicals?.filter(m => favoriteIds?.has(m?.id));
  }, [allMusicals, favoriteIds]);

  const handleRetryFavorites = useCallback(() => {
    if (!user?.id) return;
    setFavoritesLoading(true);
    setFavoritesError(null);
    userService?.getFavoriteMusicals(user?.id)?.then(({ favorites, error: favError }) => {
        if (favError) {
          setFavoritesError('Failed to load favorites. Please try again.');
        } else if (favorites) {
          setFavoriteIds(new Set(favorites?.map(f => f?.musicalId)));
        }
      })?.catch(() => {
        setFavoritesError('Unable to load favorites. Please try again.');
      })?.finally(() => setFavoritesLoading(false));
  }, [user?.id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <Helmet>
        <title>All Musicals | RoleCall</title>
      </Helmet>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Musicals</h1>
          <p className="text-blue-200 mt-1 text-sm">
            {loading ? 'Loading…' : `${allMusicals?.length} musicals in the database`}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-500 bg-opacity-20 border border-red-400 border-opacity-40 rounded-xl p-4 flex items-center space-x-3">
            <Icon name="AlertCircle" size={20} className="text-red-300 flex-shrink-0" />
            <p className="text-red-200 text-sm">{error}</p>
            <button
              onClick={() => window.location?.reload()}
              className="ml-auto text-xs text-red-300 underline hover:text-red-100"
            >
              Refresh
            </button>
          </div>
        )}

        {/* ── SECTION 0: My Favorites ── */}
        {!isSearching && user && (
          <section className="mb-10">
            <SectionHeader icon="Heart" title="My Favorites" count={favoritesLoading ? undefined : favoriteMusicals?.length} />
            {favoritesLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {Array.from({ length: 6 })?.map((_, i) => (
                  <CardSkeleton key={i} height="h-40" />
                ))}
              </div>
            ) : favoritesError ? (
              <div className="bg-red-500 bg-opacity-10 border border-red-400 border-opacity-30 rounded-xl p-8 text-center">
                <Icon name="AlertCircle" size={32} className="text-red-300 opacity-60 mx-auto mb-2" />
                <p className="text-red-200 text-sm mb-4">{favoritesError}</p>
                <button
                  onClick={handleRetryFavorites}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 bg-opacity-20 hover:bg-opacity-30 border border-red-400 border-opacity-40 text-red-200 text-sm rounded-lg transition-colors"
                >
                  <Icon name="RefreshCw" size={14} />
                  Retry
                </button>
              </div>
            ) : favoriteMusicals?.length === 0 ? (
              <div className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl p-8 text-center">
                <Icon name="Heart" size={32} className="text-blue-300 opacity-40 mx-auto mb-2" />
                <p className="text-blue-200 text-sm">No favorites yet.</p>
                <p className="text-blue-300 text-xs mt-1">Click the heart icon on any musical to add it to your favorites.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {favoriteMusicals?.map(musical => (
                  <MusicalCard
                    key={musical?.id}
                    musical={musical}
                    onClick={handleMusicalClick}
                    size="normal"
                    isFavorited={true}
                    onFavoriteToggle={handleFavoriteToggle}
                    isFeaturedMusical={featuredMusicalIds?.has(musical?.id)}
                    onFeatureToggle={isSuperUser?.() ? handleFeatureToggle : null}
                    isSuperUser={isSuperUser}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── SECTION 1: Featured Musicals ── */}
        {!isSearching && (
          <section className="mb-10">
            <SectionHeader icon="Star" title="Featured Musicals" count={loading ? undefined : featuredMusicals?.length} />
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {Array.from({ length: 6 })?.map((_, i) => (
                  <CardSkeleton key={i} height="h-56" />
                ))}
              </div>
            ) : featuredMusicals?.length === 0 ? (
              <div className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl p-8 text-center">
                <Icon name="Star" size={32} className="text-blue-300 opacity-40 mx-auto mb-2" />
                <p className="text-blue-200 text-sm">No featured musicals yet. Click the star icon on any musical to feature it here.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {featuredMusicals?.map(musical => (
                  <MusicalCard
                    key={musical?.id}
                    musical={musical}
                    onClick={handleMusicalClick}
                    size="featured"
                    isFavorited={favoriteIds?.has(musical?.id)}
                    onFavoriteToggle={user ? handleFavoriteToggle : null}
                    isFeaturedMusical={featuredMusicalIds?.has(musical?.id)}
                    onFeatureToggle={isSuperUser?.() ? handleFeatureToggle : null}
                    isSuperUser={isSuperUser}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── SECTION 2: Recently Updated ── */}
        {!isSearching && (
          <section className="mb-10">
            <SectionHeader icon="Clock" title="Recently Updated" count={loading ? undefined : recentMusicals?.length} />
            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {Array.from({ length: 6 })?.map((_, i) => (
                  <CardSkeleton key={i} height="h-40" />
                ))}
              </div>
            ) : recentMusicals?.length === 0 ? (
              <div className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl p-8 text-center">
                <Icon name="Clock" size={32} className="text-blue-300 opacity-40 mx-auto mb-2" />
                <p className="text-blue-200 text-sm">No musicals found.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {recentMusicals?.map(musical => (
                  <MusicalCard
                    key={musical?.id}
                    musical={musical}
                    onClick={handleMusicalClick}
                    size="normal"
                    isFavorited={favoriteIds?.has(musical?.id)}
                    onFavoriteToggle={user ? handleFavoriteToggle : null}
                    isFeaturedMusical={featuredMusicalIds?.has(musical?.id)}
                    onFeatureToggle={isSuperUser?.() ? handleFeatureToggle : null}
                    isSuperUser={isSuperUser}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── SECTION 3: Search All Musicals ── */}
        <section>
          <SectionHeader
            icon="Search"
            title={isSearching ? `Search Results` : 'Browse All Musicals'}
            count={isSearching ? searchResults?.length : (loading ? undefined : remainingMusicals?.length)}
          />

          {/* Search Input */}
          <div className="relative mb-5">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Icon name="Search" size={18} className="text-blue-300" />
            </div>
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search by title, composer, lyricist, genre…"
              className="w-full pl-11 pr-10 py-3 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-xl text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
            />
            {searchInput && (
              <button
                onClick={handleSearchClear}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-blue-300 hover:text-white transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>

          {/* Results */}
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 })?.map((_, i) => <RowSkeleton key={i} />)}
            </div>
          ) : isSearching ? (
            searchResults?.length === 0 ? (
              <div className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl p-10 text-center">
                <Icon name="SearchX" size={36} className="text-blue-300 opacity-40 mx-auto mb-3" />
                <p className="text-blue-200 text-sm">No musicals match &ldquo;{searchTerm}&rdquo;</p>
                <button
                  onClick={handleSearchClear}
                  className="mt-3 text-xs text-blue-300 underline hover:text-blue-100"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {searchResults?.map(musical => (
                  <MusicalRowItem
                    key={musical?.id}
                    musical={musical}
                    onClick={handleMusicalClick}
                    isFavorited={favoriteIds?.has(musical?.id)}
                    onFavoriteToggle={user ? handleFavoriteToggle : null}
                    isFeaturedMusical={featuredMusicalIds?.has(musical?.id)}
                    onFeatureToggle={isSuperUser?.() ? handleFeatureToggle : null}
                    isSuperUser={isSuperUser}
                  />
                ))}
              </div>
            )
          ) : (
            remainingMusicals?.length === 0 ? (
              <div className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl p-10 text-center">
                <Icon name="Music" size={36} className="text-blue-300 opacity-40 mx-auto mb-3" />
                <p className="text-blue-200 text-sm">All musicals are shown in the sections above.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {remainingMusicals?.map(musical => (
                  <MusicalRowItem
                    key={musical?.id}
                    musical={musical}
                    onClick={handleMusicalClick}
                    isFavorited={favoriteIds?.has(musical?.id)}
                    onFavoriteToggle={user ? handleFavoriteToggle : null}
                    isFeaturedMusical={featuredMusicalIds?.has(musical?.id)}
                    onFeatureToggle={isSuperUser?.() ? handleFeatureToggle : null}
                    isSuperUser={isSuperUser}
                  />
                ))}
              </div>
            )
          )}
        </section>

      </div>
    </div>
  );
}